import type { RouterOutputs } from '@poseidon/api/types';
import { cn } from '@poseidon/components/cn';
import type { TipTapDocument, TipTapVariable } from '@poseidon/components/Input2/TipTapInput';
import { Development } from '@poseidon/components/StrokeIcons';

import { SequenceGraphEditor } from '~/components/Sequence/SequenceGraphEditor';
import type { SequencePersonIdentity } from '~/components/Sequence/SequenceNodeFaces';

type ContactSequenceGraph = RouterOutputs['sequence']['enrollmentGraphsForContact']['enrollments'][number];

/** Human labels for the coarse enrollment status (the same lifecycle the People list shows). */
const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  waiting: 'Waiting',
  blocked: 'Blocked',
  human: 'Needs review',
  errored: 'Errored',
};

interface Props {
  enrollment: ContactSequenceGraph;
  teamId: string;
  /** The open contact — the ONE person this graph is about; pinned to the step they're currently on. */
  contact: SequencePersonIdentity;
  /** `@`-variables + `/`-documents for the per-step content editor (this contact's values). */
  variables?: TipTapVariable[];
  documents?: TipTapDocument[];
  /** Persist a per-contact body/subject override for one step (keyed by sequenceOverrideKey). */
  onSaveStepContent: (overrideKey: string, next: { body?: string; subject?: string }) => Promise<void>;
  /** Mark the call/reminder to-do this contact is parked on done — advances the arm (by manual-task wait node id). */
  onCompleteManualStep: (nodeId: string) => Promise<void>;
}

/**
 * One active sequence enrollment on the contact detail page, drawn as the LOCKED touch-sequence graph — the
 * same node canvas the builder uses, but structure-frozen: delays / order / branches can't change, and
 * clicking a send step edits only its body/subject as a per-contact override (never the template). The
 * graph highlights where this contact currently is and locks steps already sent.
 */
export function ContactSequenceGraphCard({
  enrollment,
  teamId,
  contact,
  variables,
  documents,
  onSaveStepContent,
  onCompleteManualStep,
}: Props) {
  return (
    <div className="overflow-hidden rounded-xl border border-lynch-200 dark:border-slate-700">
      <div className="flex items-center gap-2 border-b border-lynch-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-900">
        <Development.Dataflow04 className="size-4 shrink-0 text-aurora-500" />
        <span className="truncate text-sm font-semibold text-slate-700 dark:text-slate-200">{enrollment.sequenceName}</span>
        <span
          className={cn(
            'ml-auto shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
            enrollment.status === 'errored'
              ? 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-300'
              : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300',
          )}>
          {STATUS_LABEL[enrollment.status] ?? enrollment.status}
        </span>
      </div>
      {/* The pan/zoom canvas needs a sized parent, and a branching graph is only readable with room to
          breathe — so it takes a share of the viewport rather than a fixed box. The wheel still scrolls the
          page past it (SequenceGraphEditor turns `wheelPan` off in this mode), which is what makes a canvas
          this tall safe to inline in the detail panel. */}
      <div className="relative h-[70vh] min-h-[420px] bg-white dark:bg-stone-900">
        <SequenceGraphEditor
          readOnly
          teamId={teamId}
          sequenceId={enrollment.sequencePublicId}
          initialSteps={enrollment.steps}
          owner={{
            userId: enrollment.ownerUserId,
            name: enrollment.ownerName,
            profilePicture: enrollment.ownerProfilePicture,
          }}
          overrides={enrollment.overrides}
          stepStatus={enrollment.stepStatus}
          contact={contact}
          onSaveStepContent={onSaveStepContent}
          onCompleteManualStep={onCompleteManualStep}
          variables={variables}
          documents={documents}
        />
      </div>
    </div>
  );
}
